/**
 * Header nav links that point at the page you're already on are marked
 * aria-current="page" (Liquid sets it when link.current). Clicking one
 * shouldn't trigger a full reload — it smoothly scrolls back up instead.
 *
 * On a product page the product's own nav link (e.g. "Get Cuttea") scrolls to
 * the top of the media gallery, offset for the sticky header; on other pages it
 * scrolls to the top. The mobile menu drawer is closed first so the scroll is
 * visible. Respects prefers-reduced-motion.
 *
 * NB: the scroll container is not always the window. At >=990px base.css locks
 * html/body to `overflow: hidden` and makes `.page-wrapper` the scroller, so we
 * resolve the gallery's real scrollable ancestor rather than assuming window.
 */

const HEADER = '.header-section, header-component';
const GALLERY = '.product-information__media';
const NAV_SCOPE = '.header, .menu-drawer, header-component';

/** Wait past the drawer close (--drawer-animation-speed 0.2s) so scroll-lock releases. */
const DRAWER_CLOSE_MS = 260;

/**
 * The nearest scrollable ancestor of `el`, or null when the window scrolls.
 * @param {Element | null | undefined} el
 * @returns {HTMLElement | null}
 */
function scrollableAncestor(el) {
  let node = el?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 2) {
      return /** @type {HTMLElement} */ (node);
    }
    node = node.parentElement;
  }
  return null;
}

/** @param {ScrollBehavior} behavior */
function scrollToAnchor(behavior) {
  const header = document.querySelector(HEADER);
  const offset = (header instanceof HTMLElement ? header.getBoundingClientRect().height : 0) + 8;
  const gallery = document.querySelector(GALLERY);

  if (!gallery) {
    // Not a product page — scroll whatever holds the page back to the top.
    const wrapper = document.querySelector('.page-wrapper');
    const scroller = wrapper instanceof HTMLElement && wrapper.scrollHeight > wrapper.clientHeight + 2 ? wrapper : null;
    (scroller ?? window).scrollTo({ top: 0, behavior });
    return;
  }

  const rect = gallery.getBoundingClientRect();
  const scroller = scrollableAncestor(gallery);
  if (scroller) {
    const top = scroller.scrollTop + rect.top - scroller.getBoundingClientRect().top - offset;
    scroller.scrollTo({ top: Math.max(0, top), behavior });
  } else {
    window.scrollTo({ top: Math.max(0, rect.top + window.scrollY - offset), behavior });
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('a[aria-current="page"]');
  if (!link || !link.closest(NAV_SCOPE)) return;

  // The link points at this exact page — don't reload, scroll instead.
  event.preventDefault();

  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  const drawer = /** @type {any} */ (document.querySelector('header-drawer'));
  let drawerOpen = false;
  try {
    drawerOpen = Boolean(drawer && drawer.isOpen);
  } catch {
    drawerOpen = false;
  }

  if (drawerOpen) {
    drawer.close();
    setTimeout(() => scrollToAnchor(behavior), DRAWER_CLOSE_MS);
  } else {
    scrollToAnchor(behavior);
  }
});
